import React, { useEffect, useState } from 'react'
import { Translate } from 'react-localize-redux'
import groupPlayersByPosition from 'utils/groupPlayersByPosition'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  matches as matchesModule,
  players as playersModule,
} from 'redux/modules'
import { Box, Button, Typography } from '@material-ui/core'
import CircularSpinner from 'components/loader/CircularSpinner'
import AddPlayersDialog from 'containers/TeamDashboard/components/AddPlayersDialog'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import useTeamFeature from 'hooks/useTeamFeature'
import PlayersListCard from './PlayersListCard'
import PlayersTable from './PlayersTable'
import PlayerUnavailableAlertDialog from './PlayerUnavailableAlertDialog'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const PlayersListsContainer = ({
  players,
  currentMatchUuid,
  updateMatchParticipantsInBulk,
  teamUuid,
  createTeamPlayer,
  fetchMatch,
}) => {
  const [loading, setLoading] = useState(false)
  const [loadingcircular, setLoadingCircular] = useState(false)
  const [openAddPlayers, setOpenAddPlayers] = useState(false)

  const featureGuard = useFeatureAccessGuard()
  const { enabled: hasAdvancedMatchFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_match_features_enable
  )

  const { goalkeepers, defenders, midfielders, strikers } =
    groupPlayersByPosition(
      players.map((p) => {
        return {
          uuid: p.uuid,
          player_uuid: p.relationships.player?.uuid,
          first_name: p.relationships.player?.first_name,
          last_name: p.relationships.player?.last_name,
          number: p.relationships.player?.number,
          avatar: p.relationships.player?.relationships?.avatar?.full_url,
          availability: p.availability,
          participant: p.participant,
          tag: p.tag,
          reason: p.reason,
          in_roster: p.in_roster,
          position: p.relationships.player?.position,
        }
      })
    )

  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [openAlert, setOpenAlert] = useState(false)

  const handleSelectPlayer = (player) => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    setSelectedPlayers((prev) => {
      const exists = prev.some((p) => p.uuid === player.uuid)

      if (exists) {
        // flip in_roster for existing roster
        return prev.map((p) =>
          p.uuid === player.uuid ? { ...p, in_roster: !p.in_roster } : p
        )
      }

      // add player to roster with in_roster = true
      return [...prev, { ...player, in_roster: true }]
    })
  }

  useEffect(() => {
    setSelectedPlayers(players.filter((p) => p.in_roster))
  }, [players])

  const handleUpdateRosterConfirm = (doNotShowAgain = false) => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    setLoading(true)
    setOpenAlert(false)

    if (doNotShowAgain) {
      localStorage.setItem('dont_show_unavailable_player_alert', 'true')
    }

    updateMatchParticipantsInBulk(currentMatchUuid, {
      participants: selectedPlayers.map((p) => {
        return {
          uuid: p.uuid,
          in_roster: p.in_roster ? 1 : 0,
        }
      }),
    })
      .then(() => {
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const handleUpdateRosterClick = () => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    const dontShowAlert =
      localStorage.getItem('dont_show_unavailable_player_alert') || false

    if (
      selectedPlayers.some(
        (p) => p.availability === 'NOT_AVAILABLE' && p.in_roster
      ) &&
      !dontShowAlert
    ) {
      setOpenAlert(true)
    } else {
      handleUpdateRosterConfirm()
    }
  }

  const handleAlertClose = () => {
    setOpenAlert(false)
  }

  const handleAddPlayersDialogClose = () => {
    setOpenAlert(false)
    setOpenAddPlayers(false)
  }

  const fetchCurrentMatch = () => {
    fetchMatch(currentMatchUuid).catch(() => {
      throw Error('Error in player API.')
    })
  }

  const handlePlayerSave = (data) => {
    setLoadingCircular(true)

    createTeamPlayer(teamUuid, {
      ...data,
      number: +data.number,
    })
      .then(() => {
        fetchCurrentMatch()
        setLoadingCircular(false)
      })
      .catch((error) => {
        setLoadingCircular(false)
        throw Error(error)
      })
  }

  return (
    <>
      {players.length === 0 && (
        <Box p={12} textAlign="center" style={{ height: '80vh' }}>
          <Typography variant="h5" gutterBottom>
            <Translate id="matches.invite-team-text" />
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setOpenAddPlayers(true)}
            >
              <Translate id="team-invite.team-invite-tab" />
            </Button>
          </Box>
        </Box>
      )}
      {loading && <CircularSpinner />}
      {openAlert && (
        <PlayerUnavailableAlertDialog
          open={openAlert}
          onClose={handleAlertClose}
          onSuccess={handleUpdateRosterConfirm}
        />
      )}
      {openAddPlayers && (
        <AddPlayersDialog
          open={openAddPlayers}
          onClose={handleAddPlayersDialogClose}
          handleSave={handlePlayerSave}
          loadingcircular={loadingcircular}
        />
      )}
      {players.length > 0 && (
        <>
          {goalkeepers && (
            <PlayersListCard title={<Translate id="matches.goalkeeper" />}>
              <PlayersTable
                matchPlayers={goalkeepers}
                handleSelectPlayer={handleSelectPlayer}
                selectedPlayers={selectedPlayers}
              />
            </PlayersListCard>
          )}

          {defenders && (
            <PlayersListCard title={<Translate id="matches.defender" />}>
              <PlayersTable
                matchPlayers={defenders}
                handleSelectPlayer={handleSelectPlayer}
                selectedPlayers={selectedPlayers}
              />
            </PlayersListCard>
          )}

          {midfielders && (
            <PlayersListCard title={<Translate id="matches.midfield" />}>
              <PlayersTable
                matchPlayers={midfielders}
                handleSelectPlayer={handleSelectPlayer}
                selectedPlayers={selectedPlayers}
              />
            </PlayersListCard>
          )}
          {strikers && (
            <PlayersListCard title={<Translate id="matches.striker" />}>
              <PlayersTable
                matchPlayers={strikers}
                handleSelectPlayer={handleSelectPlayer}
                selectedPlayers={selectedPlayers}
              />
            </PlayersListCard>
          )}

          <Box mt={1}>
            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={handleUpdateRosterClick}
              disabled={selectedPlayers.length === 0 || loading}
            >
              <Translate id="matches.update-roster" />
            </Button>
          </Box>
        </>
      )}
    </>
  )
}

PlayersListsContainer.defaultProps = {
  players: [],
}

PlayersListsContainer.propTypes = {
  players: PropTypes.arrayOf(PropTypes.shape({})),
  currentMatchUuid: PropTypes.string.isRequired,
  updateMatchParticipantsInBulk: PropTypes.func.isRequired,
  teamUuid: PropTypes.string.isRequired,
  createTeamPlayer: PropTypes.func.isRequired,
  fetchMatch: PropTypes.func.isRequired,
}

export default connect(
  ({ matches, team }) => ({
    currentMatchUuid: matches.current.uuid,
    teamUuid: team.uuid,
  }),
  {
    updateMatchParticipantsInBulk: matchesModule.updateMatchParticipantsInBulk,
    createTeamPlayer: playersModule.createTeamPlayer,
    fetchMatch: matchesModule.fetchMatch,
  }
)(PlayersListsContainer)
